<template>
  <main>
    <div v-if="pending" class="text-center" style="transition: none !important;">
      <b-spinner />
    </div>

    <div v-show="!pending">
      <header class="justify-between align-items-stretch mb-3">
        <b-breadcrumb :items="breadcrumb" class="flex-grow-1 mb-0" />

        <b-dropdown
            v-if="$gate.allow('create', 'floor') || $gate.allow('delete', item)"
            :id="'floor_actions_dropdown_' + id"
            :title="$t('Actions')"
            class="ml-3"
            no-caret
            right
        >
          <template slot="button-content">
            <fa fixed-width icon="ellipsis-v" />
          </template>


          <complete-services
              :api-url="`/api/floors/${id}/services_to_complete`"
              :project-id="+projectId"
          >
            <template #activator="{toggle}">
              <b-dropdown-item @click="toggle">
                <fa fixed-width icon="check" />
                {{ $t('Complete services') }}
              </b-dropdown-item>
            </template>

            <template #services="{form, services}">
              <tree-view
                  v-model="form.walls"
                  :errors="form.errors"
                  :items="services.walls"
                  :label="$t('Walls')"
                  class="mt-3"
                  error-prefix="rooms"
                  label-by="name"
              />

              <tree-view
                  v-model="form.elements"
                  :errors="form.errors"
                  :items="services.elements"
                  :label="$t('Elements')"
                  class="mt-3"
                  error-prefix="elements"
                  label-by="name"
              />
            </template>
          </complete-services>

          <b-dropdown-divider />

          <b-dropdown-item-button
              v-if="$gate.allow('create', 'floor')"
              v-b-modal.duplicate-modal
              :disabled="item.busy"
          >
            <fa fixed-width icon="copy" />
            {{ $t('Duplicate') }}
          </b-dropdown-item-button>

          <b-dropdown-divider />

          <b-dropdown-item
              v-if="$gate.allow('delete', item)"
              variant="danger"
              @click="handleDelete"
          >
            <fa fixed-width icon="trash" />
            {{ $t('Delete') }}
          </b-dropdown-item>
        </b-dropdown>
      </header>

      <duplicate-modal
          v-if="$gate.allow('create', 'floor')"
          id="duplicate-modal"
          :api-url="duplicateUrl"
          :project-id="projectId"
          @duplicated="handleDuplicated"
      />

      <b-card :title="$t('Progress')" class="mb-3">
        <b-card-sub-title class="mb-3">
          <strong>{{ completedDuration.text }}</strong>
          /
          <strong>{{ plannedDuration.text }}</strong>
        </b-card-sub-title>

        <b-progress :max="plannedDuration.value">
          <b-progress-bar :value="completedDuration.value" />
        </b-progress>
      </b-card>

      <b-card
          :header="$t('Floor')"
          class="mb-3"
          no-body
      >
        <b-form
            @keydown="item.onKeydown($event)"
            @submit.prevent="handleSave"
        >
          <b-card-body>
            <b-form-group
                :invalid-feedback="item.errors.get('name')"
                :label="$t('Name')"
                :state="item.errors.has('name') ? false : null"
                label-for="name"
            >
              <b-form-input
                  id="name"
                  v-model="item.name"
                  :readonly="$gate.deny('update', item)"
                  :state="item.errors.has('name') ? false : null"
                  autofocus
                  name="name"
              />
            </b-form-group>

            <b-form-group
                :invalid-feedback="item.errors.get('short_name')"
                :label="$t('Short name')"
                :state="item.errors.has('short_name') ? false : null"
                label-for="short_name"
            >
              <b-form-input
                  id="short_name"
                  v-model="item.short_name"
                  :readonly="$gate.deny('update', item)"
                  :state="item.errors.has('short_name') ? false : null"
                  name="short_name"
                  maxlength="4"
              />
            </b-form-group>

            <b-form-group
                :invalid-feedback="item.errors.get('height')"
                :label="$t('Height')"
                :state="item.errors.has('height') ? false : null"
                label-for="height"
            >
              <b-input-group append="m">
                <b-form-input
                    id="height"
                    v-model="item.height"
                    :readonly="$gate.deny('update', item)"
                    :state="item.errors.has('height') ? false : null"
                    min="0.001"
                    name="height"
                    step="0.001"
                    type="number"
                />
              </b-input-group>
            </b-form-group>
          </b-card-body>

          <b-card-footer v-if="$gate.allow('update', item)">
            <b-button :disabled="item.busy" type="submit" variant="primary">
              {{ $t('Save') }}
            </b-button>
          </b-card-footer>
        </b-form>
      </b-card>

      <floor-overview
          :component-id="componentId"
          :floor-id="id"
          :project-id="projectId"
      />
    </div>
  </main>
</template>

<script>
import Form from 'vform';
import DuplicateModal from '@/components/DuplicateModal';
import FloorOverview from '@/components/floors/FloorOverview';
import CompleteServices from "@/components/projects/CompleteServices";
import TreeView from "@/components/TreeView";
import itemMixin from "@/mixins/itemMixin";

export default {
  name: 'Floor',
  components: {TreeView, CompleteServices, FloorOverview, DuplicateModal},
  mixins: [itemMixin],
  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
    componentId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      item: new Form({
        name: null,
        short_name: null,
        height: null,
        model_name: null
      }),
      completedDuration: {},
      plannedDuration: {},
    };
  },
  computed: {
    resourceName() {
      return 'floors';
    },
    baseUrl() {
      return `/api/projects/${this.projectId}/components/${this.componentId}/floors`;
    },
  },
  methods: {
    afterDelete() {
      this.$router.push({
        name: 'projects.components.show',
        params: {
          projectId: this.projectId,
          componentId: this.componentId
        },
      });
    },

    handleDuplicated(data) {
      this.$router.push({
        name: 'projects.components.floors.show',
        params: {
          projectId: this.projectId,
          componentId: this.componentId,
          floorId: data.id,
        },
      });
    },

    setData(data) {
      this.completedDuration = data.completedDuration;
      this.plannedDuration = data.plannedDuration;
      this.breadcrumb = [
        {
          text: this.$t('Projects'),
          to: {name: 'projects.index'}
        }
      ].concat(data.breadcrumb);
      this.item.fill(data);
    },
  },
};
</script>
